import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  FileReport,
  FileReportCategoryOption,
  FileReportsState,
  FileReportsFilters,
  FileReportsPagination,
} from '@/types/reports.types';

interface FileReportsActions {
  // Data management
  setFiles: (files: FileReport[]) => void;
  addFiles: (files: FileReport[]) => void;
  clearFiles: () => void;

  // Loading states
  setLoading: (loading: boolean) => void;
  setLoadingMore: (loading: boolean) => void;

  // Error handling
  setError: (error: string | null) => void;

  // Filters
  setFilters: (filters: Partial<FileReportsFilters>) => void;
  clearFilters: () => void;

  // Pagination
  setPagination: (pagination: Partial<FileReportsPagination>) => void;
  setHasNextPage: (hasNextPage: boolean) => void;
  setTotalCount: (totalCount: number) => void;

  // View mode
  setViewMode: (viewMode: 'grid' | 'list') => void;

  // Categories
  setCategories: (categories: string[]) => void;
  setCategoryOptions: (categoryOptions: FileReportCategoryOption[]) => void;

  // Reset state
  reset: () => void;
}

const initialState: FileReportsState = {
  files: [],
  isLoading: false,
  isLoadingMore: false,
  error: null,
  filters: {},
  pagination: {
    pageNumber: 1,
    pageSize: 60,
    dateOrder: 'desc',
  },
  hasNextPage: false,
  totalCount: 0,
  viewMode: 'grid',
  categories: [],
  categoryOptions: [],
};

export const useFileReportsStore = create<FileReportsState & FileReportsActions>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // Data management
      setFiles: (files) => set({ files }),

      addFiles: (newFiles) => {
        const currentFiles = get().files;
        const existingIds = new Set(currentFiles.map((f) => f.fileId));
        const uniqueNewFiles = newFiles.filter((f) => !existingIds.has(f.fileId));
        set({ files: [...currentFiles, ...uniqueNewFiles] });
      },

      clearFiles: () => set({ files: [] }),

      // Loading states
      setLoading: (isLoading) => set({ isLoading }),
      setLoadingMore: (isLoadingMore) => set({ isLoadingMore }),

      // Error handling
      setError: (error) => set({ error }),

      // Filters
      setFilters: (newFilters) => {
        const currentFilters = get().filters;
        set({ filters: { ...currentFilters, ...newFilters } });
      },

      clearFilters: () => set({ filters: {} }),

      // Pagination
      setPagination: (newPagination) => {
        const currentPagination = get().pagination;
        set({ pagination: { ...currentPagination, ...newPagination } });
      },

      setHasNextPage: (hasNextPage) => set({ hasNextPage }),
      setTotalCount: (totalCount) => set({ totalCount }),

      // View mode
      setViewMode: (viewMode) => set({ viewMode }),

      // Categories
      setCategories: (categories) => set({ categories }),
      setCategoryOptions: (categoryOptions) => set({ categoryOptions }),

      // Reset state
      reset: () => set(initialState),
    }),
    { name: 'FileReportsStore' }
  )
);
